import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {
  A4_HEIGHT_PX,
  A4_MARGIN_PX,
  A4_WIDTH_PX,
  contornoAPuntos,
  puntosAContorno,
  TITULOS_PASO_ESCANER,
} from './utilidades/escaner.static';
import {
  ImagenEscaner,
  PasoEscaner,
  PuntosContorno,
} from './utilidades/escaner.models';
import { ErrorDesarrollo } from 'src/app/compartido/utilidades/error.utils';
import { DomSanitizer } from '@angular/platform-browser';
import { AlertasService } from 'src/app/nucleo/utilidades/alertas.service';

@Component({
  selector: 'compartido-escaner',
  templateUrl: './escaner.component.html',
  styleUrls: ['./escaner.component.scss'],
  standalone: false,
})
export class EscanerComponent implements AfterViewInit {
  /* Fuentes habilitadas */
  @Input() habilitarImagenes: boolean = false;
  @Input() habilitarPDF: boolean = false;
  @Input() habilitarCamara: boolean = false;
  private _fuentesHabilitadas = 0;

  /* Paso actual */
  public paso: WritableSignal<PasoEscaner> = signal(PasoEscaner.FUENTE);

  /* Computer Vision */
  private _jscanify = new (window as any).jscanify();
  private _cv = (window as any).cv;

  /* Input Archivo */
  @ViewChild('input') private _inputRef!: ElementRef;

  /* Canvas */
  @ViewChild('original') private _canvasOriginalRef!: ElementRef;
  private _canvasOriginal!: HTMLCanvasElement;
  @ViewChild('contorno') private _canvasContornoRef!: ElementRef;
  private _canvasContorno!: HTMLCanvasElement;

  /* Recorte */
  private _puntosContorno: PuntosContorno | undefined;

  /* Imágenes Subidas */
  protected imagenes: ImagenEscaner[] = [];

  /* Imagen siendo editada */
  private _editando: number | undefined;

  /* PDF Generado */
  private _pdf: Blob | undefined;
  protected pdfUrl: string | undefined;

  /* Estado de carga */
  protected cargando = signal(false);

  /* Terminar procesos */
  protected terminarCaptura: () => void = () => {};
  protected terminarRecorte: () => void = () => {
    this._canvasContorno.onpointermove = null;
    this._canvasContorno.onpointerdown = null;
  };

  /* Emisor de PDF */
  protected pdf = new EventEmitter<File>();

  /* Constantes */
  protected readonly PasoEscaner = PasoEscaner;
  protected readonly TITULOS_PASO_ESCANER = TITULOS_PASO_ESCANER;

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly _sanitizer: DomSanitizer,
    private readonly _alertasService: AlertasService,
  ) {}

  ngAfterViewInit(): void {
    // Obtener elementos del DOM para los canvas
    this._canvasOriginal = this._canvasOriginalRef.nativeElement;
    this._canvasContorno = this._canvasContornoRef.nativeElement;

    // Evitar zoom al dar doble click en dispositivos táctiles
    let lastTouchEnd = 0;
    document.addEventListener(
      'touchend',
      (event) => {
        const now = Date.now();
        if (now - lastTouchEnd <= 300) event.preventDefault();
        lastTouchEnd = now;
      },
      false,
    );

    // Tirar error si no hay fuentes habilitadas
    this._fuentesHabilitadas = [
      this.habilitarImagenes,
      this.habilitarPDF,
      this.habilitarCamara,
    ].filter((h) => h).length;
    if (this._fuentesHabilitadas === 0)
      throw new ErrorDesarrollo('No hay fuentes de escaneo habilitadas.');

    // Saltar el paso de fuente si solo hay una fuente habilitada
    this.mostrarFuenteODefault();
  }

  /**
   * Muestra las opciones de fuente o la fuente por defecto, si solo hay una habilitada.
   * @autor Juan Corral
   */
  protected mostrarFuenteODefault() {
    if (this._fuentesHabilitadas === 1) {
      if (this.habilitarImagenes) this.abrirSubir('imagen');
      else if (this.habilitarPDF) this.abrirSubir('pdf');
      else if (this.habilitarCamara) this.abrirCamara$();
    } else {
      this.paso.set(PasoEscaner.FUENTE);
    }
  }

  /**
   * Abre el diálogo para subir un archivo.
   * @autor Juan Corral
   */
  protected abrirSubir(tipo: 'pdf' | 'imagen'): void {
    this.cargando.set(true);
    const input = this._inputRef.nativeElement;
    input.value = '';
    input.accept = tipo === 'pdf' ? '.pdf' : 'image/*';
    input.onchange = () => {
      if (input.files && input.files.length > 0)
        tipo === 'pdf'
          ? this.subirPDF(input.files[0])
          : this.subirImagen(input.files[0]);
    };
    input.oncancel = () => this.cargando.set(false);
    input.click();
  }

  /**
   * Cambia al paso de usar cámara.
   * @author Juan Corral
   */
  protected async abrirCamara$(): Promise<void> {
    this.cargando.set(true);

    // Obtener elementos del DOM
    const video = document.createElement('video');
    const originalCtx = this._canvasOriginal.getContext('2d')!;
    const contornoCtx = this._canvasContorno.getContext('2d')!;

    // Empezar captura de video
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'environment',
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: false,
    });
    let animationFrameId: number;

    // Mostrar video en canvas
    video.srcObject = stream;
    video.onloadedmetadata = () => {
      video.play();
      this._canvasOriginal.width = video.videoWidth;
      this._canvasOriginal.height = video.videoHeight;
      this._canvasContorno.width = video.videoWidth;
      this._canvasContorno.height = video.videoHeight;

      const renderFrame = () => {
        originalCtx.drawImage(video, 0, 0);
        const result = this._jscanify.highlightPaper(this._canvasOriginal);
        contornoCtx.drawImage(result, 0, 0);
        // contornoCtx.drawImage(video, 0, 0);
        animationFrameId = requestAnimationFrame(renderFrame);
      };
      animationFrameId = requestAnimationFrame(renderFrame);

      // Actualizar vista
      this.paso.set(PasoEscaner.USAR_CAMARA);
      this.cargando.set(false);
    };

    // Actualizar función para terminar captura
    this.terminarCaptura = () => {
      originalCtx.drawImage(video, 0, 0);
      cancelAnimationFrame(animationFrameId);
      stream.getTracks().forEach((track) => track.stop());
      video.srcObject = null;
      video.remove();
    };
  }

  /**
   * Avanza al siguiente paso.
   * @autor Juan Corral
   */
  protected pasoSiguiente(): void {
    switch (this.paso()) {
      case PasoEscaner.USAR_CAMARA:
        this.capturarImagen();
        break;
      case PasoEscaner.RECORTE:
        this.recortarImagen();
        //   break;
        // case PasoEscaner.CONFIRMAR:
        this.guardarImagen();
        break;
      case PasoEscaner.AGREGAR:
        this.generarPDF$();
        break;
      case PasoEscaner.PDF:
        this.emitirPDF();
        break;
    }
  }

  /**
   * Retrocede al paso anterior.
   * @autor Juan Corral
   */
  protected pasoAnterior(): void {
    switch (this.paso()) {
      case PasoEscaner.FUENTE:
        if (this.imagenes.length > 0) this.paso.set(PasoEscaner.AGREGAR);
        else this.pdf.emit(undefined);
        break;
      case PasoEscaner.USAR_CAMARA:
        this.terminarCaptura();
        if (this.imagenes.length > 0) this.paso.set(PasoEscaner.AGREGAR);
        else if (this._fuentesHabilitadas === 1) this.pdf.emit(undefined);
        else this.paso.set(PasoEscaner.FUENTE);
        break;
      case PasoEscaner.RECORTE:
        this.terminarRecorte();
        this._editando = undefined;
        if (this.imagenes.length > 0) this.paso.set(PasoEscaner.AGREGAR);
        else this.paso.set(PasoEscaner.FUENTE);
        break;
      case PasoEscaner.CONFIRMAR:
        this._canvasContorno.width = this._canvasOriginal.width;
        this._canvasContorno.height = this._canvasOriginal.height;
        this.agregarContorno();
        break;
      case PasoEscaner.AGREGAR:
        if (this.imagenes.length > 0)
          this._alertasService.alertarError(
            'Debes eliminar todas las imágenes para reiniciar el proceso.',
          );
        else this.paso.set(PasoEscaner.FUENTE);
        break;
      case PasoEscaner.PDF:
        this.paso.set(PasoEscaner.AGREGAR);
        break;
    }
  }

  /**
   * Sube una imagen.
   * @param {File} archivo - Archivo de imagen.
   * @autor Juan Corral
   */
  protected subirImagen(archivo: File): void {
    this.cargando.set(true);

    if (!archivo)
      throw new ErrorDesarrollo('No se ha seleccionado una imagen.');

    const lector = new FileReader();
    lector.onload = (evento) => {
      const originalCtx = this._canvasOriginal.getContext('2d')!;
      const imagen = new Image();
      imagen.onload = () => {
        this._canvasOriginal.width = imagen.width;
        this._canvasOriginal.height = imagen.height;
        this._canvasContorno.width = imagen.width;
        this._canvasContorno.height = imagen.height;
        originalCtx.drawImage(imagen, 0, 0);
        this.agregarContorno();
      };
      imagen.src = evento.target?.result as string;
    };
    lector.readAsDataURL(archivo);
  }

  /**
   * Sube un archivo PDF.
   * @param {File} archivo - Archivo PDF.
   * @author Juan Corral
   */
  protected subirPDF(archivo: File): void {
    this.cargando.set(true);
    if (!archivo) throw new ErrorDesarrollo('No se ha seleccionado un PDF.');
    this.pdf.emit(archivo);
  }

  /**
   * Captura la imagen de la cámara.
   * @author Juan Corral
   */
  protected capturarImagen(): void {
    this.cargando.set(true);
    this.terminarCaptura();
    this.agregarContorno();
  }

  /**
   * Agrega un contorno editable al canvas.
   * @param {PuntosContorno[] | null} puntosContorno [Opcional] - Puntos del contorno.
   * @autor Juan Corral
   */
  protected agregarContorno(
    puntosContorno: PuntosContorno | null = null,
  ): void {
    // Dibujar imagen original
    const contornoCtx = this._canvasContorno.getContext('2d')!;
    contornoCtx.drawImage(this._canvasOriginal, 0, 0);

    // Obtener puntos del contorno
    let puntos: PuntosContorno | null = puntosContorno;
    if (puntos === null) {
      const contour = this._jscanify.findPaperContour(
        this._cv.imread('original'),
      );
      puntos = contornoAPuntos(
        contour ? this._jscanify.getCornerPoints(contour) : undefined,
      );
    }
    this._puntosContorno = puntos;

    // Estilo del contorno
    contornoCtx.lineWidth = 5;
    contornoCtx.strokeStyle = 'blue';

    // Dibujar contorno
    contornoCtx.beginPath();
    contornoCtx.moveTo(puntos[0].x, puntos[0].y);
    for (let i = 1; i < puntos.length; i++)
      contornoCtx.lineTo(puntos[i].x, puntos[i].y);
    contornoCtx.closePath();
    contornoCtx.stroke();

    // Dibujar puntos del contorno
    const diametro = this._canvasContorno.width / 30;
    contornoCtx.beginPath();
    for (let i = 0; i < puntos.length; i++) {
      contornoCtx.moveTo(puntos[i].x, puntos[i].y);
      contornoCtx.arc(puntos[i].x, puntos[i].y, diametro, 0, 2 * Math.PI);
    }
    contornoCtx.fill();

    // Editar puntos del contorno
    if (this._canvasContorno.onpointerdown === null)
      this._canvasContorno.onpointerdown = (evento: PointerEvent) => {
        for (let i = 0; i < puntos.length; i++) {
          const width = (evento.target as HTMLElement).offsetWidth;
          const height = (evento.target as HTMLElement).offsetHeight;
          const x = (evento.offsetX * this._canvasContorno.width) / width;
          const y = (evento.offsetY * this._canvasContorno.height) / height;
          const dx = x - puntos[i].x;
          const dy = y - puntos[i].y;
          if (dx * dx + dy * dy > diametro * diametro * 3) continue;

          this._canvasContorno.onpointermove = (evento: PointerEvent) => {
            puntos[i].x = (evento.offsetX * this._canvasContorno.width) / width;
            puntos[i].y =
              (evento.offsetY * this._canvasContorno.height) / height;
            this.agregarContorno(puntos);
          };
          this._canvasContorno.onpointerup = this._canvasContorno.onpointerout =
            () => (this._canvasContorno.onpointermove = null);
          return;
        }
      };

    // Actualizar vista
    if (!puntosContorno) {
      this.paso.set(PasoEscaner.RECORTE);
      this.cargando.set(false);
    }
  }

  /**
   * Captura la imagen de la cámara.
   * @author Juan Corral
   */
  protected recortarImagen(): void {
    this.cargando.set(true);

    this.terminarRecorte();
    const width = Math.max(
      this._puntosContorno![1].x - this._puntosContorno![0].x,
      this._puntosContorno![2].x - this._puntosContorno![3].x,
    );
    const height = Math.max(
      this._puntosContorno![3].y - this._puntosContorno![0].y,
      this._puntosContorno![2].y - this._puntosContorno![1].y,
    );
    const result = this._jscanify.extractPaper(
      this._canvasOriginal,
      width,
      height,
      puntosAContorno(this._puntosContorno!),
    );
    const contornoCtx = this._canvasContorno.getContext('2d')!;
    this._canvasContorno.width = width;
    this._canvasContorno.height = height;
    contornoCtx.drawImage(result, 0, 0);

    this.paso.set(PasoEscaner.CONFIRMAR);
    this.cargando.set(false);
  }

  /**
   * Guarda la imagen recortada.
   * @autor Juan Corral
   */
  protected guardarImagen(): void {
    this.cargando.set(true);

    if (this._editando !== undefined) {
      this.imagenes[this._editando] = {
        original: this._canvasOriginal.toDataURL(),
        recortada: this._canvasContorno.toDataURL(),
      };
      this._editando = undefined;
    } else {
      this.imagenes.push({
        original: this._canvasOriginal.toDataURL(),
        recortada: this._canvasContorno.toDataURL(),
      });
    }

    this.paso.set(PasoEscaner.AGREGAR);
    this.cargando.set(false);
  }

  /**
   * Vuelve a editar una imagen recortada.
   * @param {number} indice - Índice de la imagen a editar.
   * @autor Juan Corral
   */
  protected editarImagen(indice: number): void {
    this.cargando.set(true);

    const imagen = this.imagenes[indice];
    const originalCtx = this._canvasOriginal.getContext('2d')!;
    const imagenOriginal = new Image();
    imagenOriginal.onload = () => {
      this._canvasOriginal.width = imagenOriginal.width;
      this._canvasOriginal.height = imagenOriginal.height;
      this._canvasContorno.width = imagenOriginal.width;
      this._canvasContorno.height = imagenOriginal.height;
      originalCtx.drawImage(imagenOriginal, 0, 0);
      this._editando = indice;
      this.agregarContorno();
    };
    imagenOriginal.src = imagen.original;
  }

  /**
   * Genera un archivo PDF a partir de la imagen del canvas.
   * @autor Juan Corral
   */
  protected async generarPDF$(): Promise<void> {
    this.cargando.set(true);

    // Crear el PDF
    const pdf = new (window as any).jsPDF('p', 'px', [
      A4_WIDTH_PX,
      A4_HEIGHT_PX,
    ]);

    // Agregar imágenes al PDF
    for (let i = 0; i < this.imagenes.length; i++) {
      const imagen = this.imagenes[i];
      if (i > 0) pdf.addPage();
      await this._agregarImagenPDF$(pdf, imagen);
    }

    // Guardar PDF
    this._pdf = await pdf.output('blob');
    this.pdfUrl = await pdf.output('dataurlstring');

    // Actualizar vista a la pantalla de vista previa
    this.paso.set(PasoEscaner.PDF);
    this.cargando.set(false);
  }

  /**
   * Agrega una imagen al PDF.
   * @param {any} pdf - Instancia de jsPDF.
   * @param {ImagenEscaner} imagen - Imagen a agregar.
   * @autor Juan Corral
   */
  private async _agregarImagenPDF$(
    pdf: any,
    imagen: ImagenEscaner,
  ): Promise<void> {
    return new Promise<void>((resolver) => {
      const img = new Image();
      img.onload = () => {
        let processedImgData = img.src;

        if (false) {
          // Crear OpenCV Mat
          const src = this._cv.imread(img);

          // Convertir a escala de grises
          let gris = new this._cv.Mat();
          this._cv.cvtColor(src, gris, this._cv.COLOR_RGBA2GRAY, 0);

          // Aplicar umbral adaptativo
          let umbral = new this._cv.Mat();
          this._cv.adaptiveThreshold(
            gris,
            umbral,
            255,
            this._cv.ADAPTIVE_THRESH_GAUSSIAN_C,
            this._cv.THRESH_BINARY,
            13,
            13,
          );

          // Convertir a imagen
          this._cv.imshow(this._canvasContorno, umbral);
          processedImgData = this._canvasContorno.toDataURL(
            'image/jpeg',
            1.0,
          );

          // Liberar memoria
          src.delete();
          gris.delete();
          umbral.delete();
        }

        // Calcular dimensiones del PDF
        const imgWidth = img.width;
        const imgHeight = img.height;
        const availableWidth = A4_WIDTH_PX - 2 * A4_MARGIN_PX;
        const availableHeight = A4_HEIGHT_PX - 2 * A4_MARGIN_PX;

        // Calcular escala para mantener la proporción
        const scale = Math.min(
          availableWidth / imgWidth,
          availableHeight / imgHeight,
        );
        const scaledWidth = imgWidth * scale;
        const scaledHeight = imgHeight * scale;

        // Calcular posición centrada
        const xPos = (A4_WIDTH_PX - scaledWidth) / 2;
        const yPos = (A4_HEIGHT_PX - scaledHeight) / 2;

        // Agregar imagen al PDF
        pdf.addImage(
          processedImgData,
          'JPEG',
          xPos,
          yPos,
          scaledWidth,
          scaledHeight,
        );
        resolver();
      };
      img.src = imagen.recortada;
    });
  }

  /**
   * Emite el archivo PDF generado.
   * @autor Juan Corral
   */
  protected emitirPDF(): void {
    if (!this._pdf) throw new ErrorDesarrollo('No se ha generado el PDF.');
    this.pdf.emit(new File([this._pdf!], 'documento.pdf'));
  }

  /**
   * Procesa la imagen del canvas para extraer texto y realizar OCR.
   * @autor Juan Corral
   */
  protected leerImagen(): void {
    // Extraer texto
    // recognize(this.imagen$.value, 'spa')
    //   .then(({ data: { text } }) => {
    //     console.log('Extracted text:', text);
    //   })
    //   .catch((error) => {
    //     console.error('OCR error:', error);
    //   });
  }
}
